import { faMoneyCheckDollar } from '@fortawesome/pro-solid-svg-icons'
import { AnimatePresence } from 'motion/react'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { Dropdown } from '~/components/Form/Input/Dropdown'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { CompaniesAutocomplete } from '~/domains/companies/components/CompaniesAutocomplete'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { themeClass } from '~/styles/theme.css'
import { useInvoiceListWithFilters } from '../queries/list'
import { type InvoiceListFilters, InvoiceStatusLabels } from '../types'
import { InvoiceCard } from './InvoiceCard'
import { filtersRow, resultsWrapper } from './InvoiceList.css'
import { InvoiceModal } from './InvoiceModal'

interface InvoiceListProps {
  filters: InvoiceListFilters
  quicksearch?: string
  onResetFilters?: () => void
  onFiltersChange?: (filters: InvoiceListFilters) => void
  hideCompanyFilter?: boolean
  hideInlineFilters?: boolean
  embedded?: boolean
}

export const InvoiceList = ({
  filters: initialFilters,
  quicksearch = '',
  onFiltersChange,
  hideCompanyFilter = false,
  hideInlineFilters = false,
  embedded = false
}: InvoiceListProps): ReactElement => {
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null)

  const {
    invoicelist,
    isInvoiceListLoading,
    isInvoiceListError,
    fetchNextInvoiceListPage,
    hasNextInvoiceListPage,
    isFetchingNextInvoiceListPage
  } = useInvoiceListWithFilters({
    filters: initialFilters,
    itemsPerPage: embedded ? 6 : 30,
    enabled: true
  })

  const endOfListReached = useCallback(() => {
    if (hasNextInvoiceListPage && !isFetchingNextInvoiceListPage) {
      fetchNextInvoiceListPage()
    }
  }, [
    hasNextInvoiceListPage,
    isFetchingNextInvoiceListPage,
    fetchNextInvoiceListPage
  ])

  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached
  })

  const filteredInvoices = useMemo(() => {
    const invoices = invoicelist ?? []
    let result = [...invoices]

    if (quicksearch) {
      result = result.filter((invoice) => {
        const search = quicksearch.toLowerCase()
        return (
          invoice.id.toString().includes(search) ||
          (typeof invoice.type !== 'string' &&
            invoice.type.name.toLowerCase().includes(search)) ||
          (typeof invoice.order !== 'string' &&
            invoice.order.company?.name.toLowerCase().includes(search)) ||
          invoice.contact?.company?.name.toLowerCase().includes(search)
        )
      })
    }

    return result
  }, [invoicelist, quicksearch])

  return (
    <div className={themeClass}>
      {editingInvoiceId && (
        <InvoiceModal
          open={true}
          onClose={() => setEditingInvoiceId(null)}
          invoiceId={editingInvoiceId}
        />
      )}

      {!hideInlineFilters && (
        <div className={filtersRow}>
          {!hideCompanyFilter && (
            <CompaniesAutocomplete
              small
              fullWidth
              placeholder="Filtrer par Entreprise"
              ids={
                initialFilters.companyId
                  ? [String(initialFilters.companyId)]
                  : []
              }
              onChange={(value) => {
                if (onFiltersChange) {
                  onFiltersChange({
                    ...initialFilters,
                    companyId: value ? Number(value) : undefined
                  })
                }
              }}
            />
          )}

          <Dropdown
            key={`status-${initialFilters.status}`}
            options={Object.entries(InvoiceStatusLabels).map(
              ([value, label]) => ({
                label,
                value
              })
            )}
            small
            fullWidth
            emptyLabel="Tous les statuts"
            defaultValue={
              initialFilters.status ? String(initialFilters.status) : ''
            }
            onChange={(value) => {
              if (onFiltersChange) {
                onFiltersChange({
                  ...initialFilters,
                  status: value && value !== '' ? value : undefined
                })
              }
            }}
          />
        </div>
      )}

      <div className={resultsWrapper}>
        <AnimatePresence>
          {isInvoiceListError && (
            <Alert
              variation={AlertVariation.Danger}
              message="Une erreur est survenue lors de la récupération des factures."
              onDismiss={false}
            />
          )}
          {filteredInvoices.length === 0 && !isInvoiceListLoading && (
            <NoResult
              icon={faMoneyCheckDollar}
              message="Aucune facture"
              isSmall={embedded}
            />
          )}
          {filteredInvoices.map((invoice) => (
            <InvoiceCard
              key={`invoice${invoice.id}`}
              invoice={invoice}
              embedded={embedded}
              onEdit={setEditingInvoiceId}
            />
          ))}
          {(isInvoiceListLoading || isFetchingNextInvoiceListPage) &&
            [...Array(5)].map((_, i) => (
              <Shimmer
                width="100%"
                height={71}
                // biome-ignore lint/suspicious/noArrayIndexKey: Shimmer cant have unique key
                key={i + (filteredInvoices.length ?? 0)}
              />
            ))}
          {hasNextInvoiceListPage && embedded && (
            <ButtonGroup align="center">
              <Button
                onClick={fetchNextInvoiceListPage}
                disabled={isFetchingNextInvoiceListPage}
                variation={ButtonVariation.Neutral}
                type="button"
                fullWidth
              >
                Charger plus de factures
              </Button>
            </ButtonGroup>
          )}
        </AnimatePresence>
      </div>
      {!embedded && <div ref={lastItemRef} />}
    </div>
  )
}
